import React from "react";
import { Form, Row, Col, ButtonGroup, ToggleButton, Button } from 'react-bootstrap';
import {Metrics, DateGroup, EnvList} from './AnalyticsCommon'
import {TbTableExport} from 'react-icons/tb';

import "./Analytics.css"

const TimePeriod = {
    Week: 'Week',
    Month: 'Month',
    Year: 'Year'
}

function makePeriod(dateGroup: string, timePeriod: string) {
    let begin = null
    switch(timePeriod) {
        case TimePeriod.Week:
            begin = new Date()
            begin.setDate(begin.getDate() - 7)
            begin.setHours(0, 0, 0, 0)
            break
        case TimePeriod.Month:
            begin = new Date()
            begin.setMonth(begin.getMonth() - 1)
            begin.setHours(0, 0, 0, 0)
            break
        case TimePeriod.Year:
            begin = new Date()
            begin.setFullYear(begin.getFullYear() - 1)
            begin.setHours(0, 0, 0, 0)
            break
        default:
            throw `Invalid time period: [${timePeriod}]`
    }

    switch(dateGroup) {
        case DateGroup.Weekly:
            while (begin.getDay() !== 1) {
                begin.setDate(begin.getDate() - 1);
            }
        break
        case DateGroup.Monthly:
            begin.setDate(1);
            break
        default: break
    }

    //let yesterdayMidnight = new Date()
    //yesterdayMidnight.setDate(yesterdayMidnight.getDate() - 1)
    //yesterdayMidnight.setHours(23, 59, 59, 999)

    let now = new Date()
    return [begin, now]
}

function requiresObjectId(metric: string) {
    if (metric.startsWith('LIVE_DUMMY_')) {
        return true
    }

    if (metric.startsWith('WEB_MAP_ITEM_')) {
        return true
    }

    return false
}

function metricToEvent(metric: string) {
    switch (metric) {
        case Metrics.LIVE_VISIT: return 'EnterDimension'
        case Metrics.LIVE_NEW_USER: return 'NewDimensionUser'
        case Metrics.LIVE_DUMMY_PROXIMITY: return 'ObjectCreated'
        case Metrics.LIVE_DUMMY_VIEW: return 'ObjectLocalized'
        case Metrics.WEB_VISIT: return 'DimensionWebVisit'
        case Metrics.WEB_MAP_VIEW: return 'WebMapView'
        case Metrics.WEB_MAP_ITEM_CLICK: return 'WebMapItemClick'
        case Metrics.WEB_MAP_ITEM_LINK_CLICK: return 'WebMapItemLinkClick'
        case Metrics.WEB_MAP_ITEM_AUDIO_PLAY: return 'WebMapItemAudioPlay'
        default: break
    }
    throw `Invalid metric [${metric}] to event`
}

const AnalyticsControls: React.FC<{getAnalytics: (config: any) => void}> = (props) => {

    const [metric, setMetric] = React.useState<string>('');
    const [objectId, setobjectId] = React.useState<string>('');
    const [uniqueUsers, setUniqueUsers] = React.useState<boolean>(false);
    const [dateGroup, setDateGroup] = React.useState<string>(DateGroup.Daily);
    const [timePeriod, setTimePeriod] = React.useState<string>(TimePeriod.Week);
    const [selectedEnv, setSelectedEnv] = React.useState<string>(EnvList.prod);
    
    const [inputsValid, setInputsValid] = React.useState<boolean>(false);
    
    const [requestInProgress, setRequestInProgress] = React.useState<boolean>(false);


    React.useEffect(() => {
        setInputsValid(validateInputs())
    }, [metric, objectId]);

    function validateInputs() {
        if (!metric) {
            return false
        }
//        if (requiresObjectId(metric) && !objectId) {
//            return false
//        }
        return true
    }

    async function getAnalyticsClick() {
        if (!validateInputs()) {
            window.alert('Invalid inputs')
            return
        }

        let config = {
            metric: metric,
            event: metricToEvent(metric),
            objectId: requiresObjectId(metric) && objectId ? objectId : undefined,
            uniqueUsers: uniqueUsers,
            dateGroup: dateGroup,
            period: makePeriod(dateGroup, timePeriod),
            env: selectedEnv
        }

        setRequestInProgress(true)
        try {
            await props.getAnalytics(config)
        } catch (error) {
            console.error(`Failed to get analytics: ${error}`)
        }

        setRequestInProgress(false)
    }

    return (
        <div className="analytics-controls">
            <Row className="analytics-controls">
                <Col xs={6} md={4} className="analytics-controls-cell">
                    <div className="d-flex align-items-start mt-2">
                        <span>Select Metric:</span>
                    </div>
                    <Form.Control as="select" value={metric} onChange={(e) => setMetric(e.target.value)}>
                        <option value=''></option>
                        { Object.keys(Metrics).map((item, idx) => 
                            <option value={item} key={idx}>{item}</option>
                        )}
                    </Form.Control>
                    <Form.Control
                        type="text"
                        placeholder="ObjectId"
                        value={objectId}
                        disabled = {!requiresObjectId(metric)}
                        onChange={(e) => setobjectId(e.target.value)}
                    />
                </Col>

                <Col xs={6} md={4} className="analytics-controls-cell">
                    <div className="d-flex align-items-center mt-2">
                        <Form.Group controlId='analytics-unique-users-check'>
                            <Form.Check 
                                type="checkbox"
                                label="Unique Users"
                                checked={uniqueUsers}
                                onChange={() => setUniqueUsers(!uniqueUsers)}
                            />
                        </Form.Group>
                    </div>
                    <Form.Control as="select" value={dateGroup} onChange={(e) => setDateGroup(e.target.value)}>
                        { Object.keys(DateGroup).map((item, idx) => 
                            <option value={item} key={idx}>{item}</option>
                        )}
                    </Form.Control>
                    <ButtonGroup aria-label="time period">
                        {Object.keys(TimePeriod).map((item, idx) => (
                            <ToggleButton
                                key={idx}
                                id={`radio-${item}`}
                                type="radio"
                                variant="secondary"
                                name="radio"
                                value={item}
                                checked={timePeriod === item}
                                onClick={() => {
                                    setTimePeriod(item)}
                                }
                            >
                                {item}
                            </ToggleButton>
                        ))}
                    </ButtonGroup>
                </Col>

                <Col xs={6} md={4} className="analytics-controls-cell">
                    <Form.Control as="select" value={selectedEnv} className="mb-2" onChange={(e) => setSelectedEnv(e.target.value)}>
                        { Object.keys(EnvList).map((item, idx) => 
                            <option value={item} key={idx}>{item}</option>
                        )}
                    </Form.Control>
                    <Button variant="primary" className="mb-2" disabled={!inputsValid || requestInProgress} onClick={getAnalyticsClick}>
                        Get Analytics
                    </Button>
                    <Button variant="secondary" disabled={true}>
                        <span style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <TbTableExport size={24} style={{ marginRight: '10px' }} />
                            Export
                        </span>
                    </Button>
                </Col>
            </Row>
        </div>
    );
};

export default AnalyticsControls;
